import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Link, useLocation } from "react-router-dom";
import { LogOut, FileText, LayoutDashboard, Award, Moon, Sun, BarChart3, Shield, Menu, X, ChevronDown, MessageSquare, Upload, Home } from "lucide-react";
import { LOGO_URL, LOGO_DARK_URL } from "@/config/branding";

export default function AppHeader() {
  const { user, logout } = useAuth();
  const { dark, toggle } = useTheme();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const NAV_ITEMS = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/notification", icon: FileText, label: "New Notification" },
    { to: "/certificates", icon: Award, label: "Certificates" },
    { to: "/analytics", icon: BarChart3, label: "Analytics" },
    { to: "/sms", icon: MessageSquare, label: "SMS Centre" },
    { to: "/batch-upload", icon: Upload, label: "Batch Upload" },
    ...(user?.role === "admin" ? [{ to: "/admin", icon: Shield, label: "Admin" }] : []),
  ];

  return (
    <header className="border-b sticky top-0 z-50 backdrop-blur-md" style={{ borderColor: 'var(--border-main)', backgroundColor: dark ? 'rgba(28,28,30,0.95)' : 'rgba(255,255,255,0.95)' }} data-testid="app-header">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-2 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <img src={dark ? LOGO_DARK_URL : LOGO_URL} alt="Self Certify UK" className="h-12 sm:h-14 object-contain object-left" data-testid="app-logo" />
        </Link>

        <div className="flex items-center gap-2 sm:gap-3">
          {/* Home button */}
          <Link to="/dashboard" data-testid="header-home-btn"
            className="flex items-center justify-center px-3 py-2 rounded-xl transition-all"
            style={{ backgroundColor: location.pathname === '/dashboard' ? 'rgba(0, 163, 224, 0.1)' : (dark ? 'var(--bg-input)' : '#F2F2F7') }}>
            <Home className="w-4 h-4" style={{ color: location.pathname === '/dashboard' ? '#00A3E0' : 'var(--text-secondary)' }} />
          </Link>

          {/* Navigation dropdown */}
          <div className="relative" ref={menuRef}>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setMenuOpen(!menuOpen)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-xl transition-all"
              style={{
                fontFamily: 'Outfit, sans-serif',
                backgroundColor: menuOpen ? '#00A3E0' : (dark ? 'var(--bg-input)' : '#F2F2F7'),
                color: menuOpen ? 'white' : 'var(--text-secondary)',
              }}
            >
              {menuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
              <span className="hidden sm:inline">Menu</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${menuOpen ? "rotate-180" : ""}`} />
            </button>

            {menuOpen && (
              <div
                data-testid="nav-dropdown"
                className="absolute right-0 top-full mt-2 w-56 rounded-2xl z-50 overflow-hidden animate-fade-in-up"
                style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 8px 30px rgba(0,0,0,0.12)' }}
              >
                {NAV_ITEMS.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all border-b last:border-0"
                    style={{
                      fontFamily: 'Outfit, sans-serif',
                      borderColor: 'var(--border-light)',
                      backgroundColor: isActive(item.to) ? 'rgba(0, 163, 224, 0.08)' : 'transparent',
                      color: isActive(item.to) ? '#00A3E0' : 'var(--text-primary)',
                    }}
                  >
                    <item.icon className="w-4 h-4" style={{ color: isActive(item.to) ? '#00A3E0' : 'var(--text-muted)' }} />
                    {item.label}
                    {isActive(item.to) && <span className="ml-auto w-2 h-2 bg-[#00A3E0] rounded-full" />}
                  </Link>
                ))}

                <Link
                  to="/profile"
                  data-testid="nav-profile"
                  className="flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all border-t"
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    borderColor: 'var(--border-light)',
                    color: isActive("/profile") ? '#00A3E0' : 'var(--text-primary)',
                  }}
                >
                  <div className="w-6 h-6 bg-[#D4A537] flex items-center justify-center text-white text-[10px] font-bold rounded-full" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                    {(user?.name || "U")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-xs font-semibold">{user?.name}</p>
                    <p className="truncate text-[10px]" style={{ color: 'var(--text-muted)' }}>{user?.company}</p>
                  </div>
                </Link>
              </div>
            )}
          </div>

          {/* Dark mode */}
          <button
            data-testid="dark-mode-toggle"
            onClick={toggle}
            className="flex items-center justify-center gap-1 px-3 py-2 rounded-xl transition-all"
            style={{ backgroundColor: dark ? 'var(--bg-input)' : '#F2F2F7', color: 'var(--text-secondary)' }}
            title={dark ? "Switch to light mode" : "Switch to dark mode"}
          >
            {dark ? <Sun className="w-4 h-4 text-[#D4A537]" /> : <Moon className="w-4 h-4" />}
            <span className="text-[10px] font-semibold hidden sm:inline" style={{ fontFamily: 'Outfit, sans-serif' }}>{dark ? "Light" : "Dark"}</span>
          </button>

          {/* Sign Out */}
          <button
            data-testid="header-logout-button"
            onClick={logout}
            className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-xl transition-all hover:bg-red-50 hover:text-red-600"
            style={{ color: 'var(--text-secondary)' }}
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline text-xs font-semibold">Sign Out</span>
          </button>
        </div>
      </div>
    </header>
  );
}
